export type FieldName = 'sourceName' | 'clientReference';

export interface FormValues {
  sourceName: string;
  clientReference: string;
}

export type FormErrors = Partial<Record<FieldName, string>>;

export interface ProcessingSetting {
  id: string;
  label: string;
  value: string;
}

export interface ApiMeta {
  status: 'Success' | 'Failed';
  message: string;
  service: string;
  timeStamp: string;
}

export interface ValidationError {
  field: string;
  message: string[];
}

/** -------- Add -------- */
export interface AddCustodianRequest {
  firstName: string;
  lastName: string;
}

export interface AddCustodianResponse {
  meta: ApiMeta;
  validationErrors?: ValidationError[];
}

/** -------- List -------- */
export interface CustodianDto {
  id: number;
  firstName: string;
  middleName?: string;
  lastName: string;
  displayName: string;
  title?: string;
}

export interface CustodianListResponse {
  data?: CustodianDto[];
  meta: ApiMeta;
  validationErrors?: ValidationError[];
}

export interface Custodian {
  id: number;
  firstName: string;
  lastName: string;
  displayName: string;
}

export type ToastState = {
  open: boolean;
  message: string;
  severity: 'success' | 'error' | 'warning';
};

export type CustodianPayload = {
  firstName: string;
  lastName: string;
};

export interface SourceDetailsTabProps {
  setActiveStep: React.Dispatch<React.SetStateAction<number>>;
}

export interface IntakeCompletionOption {
  value: string;
  label: string;
}

export enum DragAndDropUploadStatus {
  InQueue = 'in-queue',
  InProcess = 'in-progress',
  Failed = 'failed',
  Completed = 'completed',
  Cancelled = 'cancelled',
}

export interface UploadSessionData {
  uploadSessionId: string;
  chunkSize: number;
  totalChunks: number;
  status: string;
}

export interface IDragAndDropItem {
  id: number;
  file: File;
  status: DragAndDropUploadStatus;
  sessionResponse?: UploadSessionData;
}

export interface UploadQueueItem {
  id: string;
  file: File;
  status: DragAndDropUploadStatus;
  progress: number;
  uploadSessionId?: string;
  chunkSize?: number;
  totalChunks?: number;
  error?: string;
  abortController?: AbortController;
  storagePath?: string;
}

export const UploadMethod = {
  Http: 'HTTP',
  HighSpeed: 'HighSpeed',
} as const;

export type UploadMethodType = (typeof UploadMethod)[keyof typeof UploadMethod];

export interface UploadFileSessionIdPayload {
  fileName: string;
  fileSize: number;
  uploadMethod: UploadMethodType;
  mimeType: string;
}

export interface UploadSessionResponse {
  data: UploadSessionData;
  meta: ApiMeta;
}

export interface UploadSessionData {
  uploadSessionId: string;
  chunkSize: number;
  totalChunks: number;
  status: string;
  uploadedChunks?: number;
  uploadedChunkNumbers?: number[];
}

export enum DeleteFileType {
  Manifest = 1,
  Passkey = 2,
}

export interface InitiateProcessingPayload {
  evidMediaId: number;
  batchingGroupId: number | null;
  quadS: string;
  safeId: number;
  receiptMethod: string;
}

export interface ManifestAndPassKey {
  fileId: string;
  fileSizeInBytes?: number;
  fileName?: string;
  message: string;
  path: string;
}

export interface InitiateProcessingResponse {
  meta: ApiMeta;
  validationErrors?: ValidationError[];
}
export const FileCatalystModalType = {
  Remove: 'remove',
  Leave: 'leave',
  CancelUpload: 'cancelUpload',
} as const;

export type FileCatalystModalType =
  (typeof FileCatalystModalType)[keyof typeof FileCatalystModalType];

export interface ProcessingSettingsData {
  processingPriority?: string;
  opticalCharacterRecognition?: string;
  geography?: string;
  destinationReviewPlatform?: string;
  timezone?: string;
  coreECI?: boolean;
  defaultDeDuplication?: string;
  aiInvestigate?: boolean;
  auroraType?: string;
}

export interface UploadCompletedData {
  uploadSessionId: string;
  fileName: string;
  fileSize: number;
  status: string;
  storagePath: string;
}

export interface ProcessingSettingsResponse {
  data?: ProcessingSettingsData;
  meta: ApiMeta;
  validationErrors?: ValidationError[];
}

interface FileResource {
  fileId: string;
  fileSizeInBytes: number;
  fileName: string;
  storagePath: string;
}

interface UploadFile {
  fileId: string;
  fileSizeInBytes: number;
  fileName: string;
  storagePath: string;
}

export interface InitiateUploadPayload {
  name?: string;
  clientReference?: string;
  hasManifest?: boolean;
  hasPasswords?: boolean;
  hasPasskey?: boolean;
  hasSpecialHandling?: boolean;
  passwords?: string[];
  custodianType?: string;
  custodianId?: string;
  custodianName?: string;
  custodianFirstName?: string;
  custodianLastName?: string;
  processingTrigger?: string;
  specialHandlingInstructions?: string;
  additionalInstructions?: boolean;
  manifests?: FileResource;
  passkeys?: FileResource[];
  uploadMethod?: UploadMethodType;
  uploads?: UploadFile[];
  auroraType: string;
}
export interface AssignCustodianPayload {
  custodianId: number;
  batchingGroup: number | null;
}

export interface DuplicateNameResponse {
  data: boolean;
  meta: ApiMeta;
  validationErrors?: ValidationError[];
}
